import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Heart, Instagram, Facebook } from 'lucide-react';
import api from '../services/api';

const Footer = () => {
    const currentYear = 2018;
    const [siteIcon, setSiteIcon] = useState(null);

    // Fetch site icon from public endpoint
    useEffect(() => {
        const fetchSiteIcon = async () => {
            try {
                const response = await api.get('/admin/settings/public');
                if (response.data.success) {
                    const iconUrl = response.data.data.site_icon;
                    if (iconUrl) {
                        setSiteIcon(iconUrl);
                    }
                }
            } catch (error) {
                console.error('Error fetching site icon:', error);
            }
        };

        fetchSiteIcon();
    }, []);

    const footerLinks = {
        Platform: [
            { name: 'Dashboard', href: '/dashboard' },
            { name: 'Discover', href: '/discover' },
            { name: 'Feed', href: '/feed' },
            { name: 'Gallery', href: '/gallery' },
        ],
        Community: [
            { name: 'Ensemble', href: '/community' },
            { name: 'Calendar', href: '/calendar' },
            { name: 'Events', href: '/calendar' },
        ],
    };

    const SpotifyIcon = () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current group-hover:scale-110 transition-transform duration-300">
            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
    );

    const YouTubeIcon = () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current group-hover:scale-110 transition-transform duration-300">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
    );

    const AppleMusicIcon = () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current group-hover:scale-110 transition-transform duration-300">
            <path d="M23.994 6.124a9.23 9.23 0 0 0-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a6.309 6.309 0 0 0-1.86-.71 10.55 10.55 0 0 0-1.872-.17H6.17a10.478 10.478 0 0 0-1.874.17 6.326 6.326 0 0 0-1.861.71C1.306 1.638.56 2.637.244 3.934a9.23 9.23 0 0 0-.24 2.19C0 6.5 0 6.748 0 12s0 5.5.004 5.876a9.23 9.23 0 0 0 .24 2.19c.316 1.31 1.062 2.31 2.18 3.043a6.309 6.309 0 0 0 1.86.71 10.55 10.55 0 0 0 1.872.17h11.672a10.478 10.478 0 0 0 1.874-.17 6.326 6.326 0 0 0 1.861-.71c1.118-.733 1.863-1.732 2.18-3.043a9.23 9.23 0 0 0 .24-2.19C24 17.5 24 17.252 24 12s0-5.5-.006-5.876zM15.998 6.4v8.2a3.6 3.6 0 0 1-1.8 3.118 3.6 3.6 0 0 1-3.6 0 3.6 3.6 0 0 1-1.8-3.118 3.6 3.6 0 0 1 3.6-3.6c.48 0 .96.093 1.4.277V6.4a.6.6 0 0 1 .6-.6h1.0a.6.6 0 0 1 .6.6z"/>
        </svg>
    );

    const socialLinks = [
        { name: 'Spotify', icon: SpotifyIcon, href: 'https://open.spotify.com/artist/2BHe1lroRC1JxpLIK5GV4o' },
        { name: 'YouTube', icon: YouTubeIcon, href: 'https://www.youtube.com/channel/UCsfc6IJH34AeiqUp5gUd-ng' },
        { name: 'Apple Music', icon: AppleMusicIcon, href: 'https://music.apple.com/us/artist/the-deep-wave/1817283591' },
        { name: 'Facebook', icon: Facebook, href: 'https://www.facebook.com/thedeepwavejams/' },
        { name: 'Instagram', icon: Instagram, href: 'https://www.instagram.com/thedeepwave' },
    ];

    return (
        <footer className="bg-gray-900 border-t border-white/10 mt-auto">
            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* Brand Section */}
                    <div className="lg:col-span-2">
                        <div className="flex items-center space-x-3 mb-6">
                            <div style={{ width: '48px', height: '48px', flexShrink: 0 }} className="rounded-lg overflow-hidden flex items-center justify-center">
                                {siteIcon ? (
                                    <img
                                        src={siteIcon}
                                        alt="The Deep Wave Icon"
                                        width="48"
                                        height="48"
                                        style={{ width: '48px', height: '48px' }}
                                        className="object-cover"
                                    />
                                ) : (
                                    <span className="text-4xl">🌊</span>
                                )}
                            </div>
                            <h2 className="font-black text-4xl gradient-text">TheDeepWave</h2>
                        </div>
                        <p className="text-gray-400 mb-6 max-w-sm">
                            TheDeepWave .com is The Deep Wave's social platform where music creators and music lovers come together 
                            to share, discover, and create the future of The Deep Wave music.
                        </p>
                        
                        {/* Social Links */}
                        <div className="flex flex-wrap gap-3">
                            {socialLinks.map((social) => {
                                const IconComponent = social.icon;
                                const isExternal = social.href.startsWith('http');
                                return (
                                    <a
                                        key={social.name}
                                        href={social.href}
                                        className="w-10 h-10 glass rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 group"
                                        title={social.name}
                                        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    >
                                        <IconComponent className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer Links */}
                    {Object.entries(footerLinks).map(([category, links]) => (
                        <div key={category} className="lg:col-span-1">
                            <h3 className="font-bold text-white mb-4">{category}</h3>
                            <ul className="space-y-3">
                                {links.map((link) => (
                                    <li key={link.name}>
                                        <Link
                                            to={link.href}
                                            className="text-gray-400 hover:text-white transition-colors duration-300 text-sm"
                                        >
                                            {link.name}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Call-to-Action Section */}
                <div className="mt-12 pt-8 border-t border-white/10">
                    <div className="glass-card p-8 text-center">
                        <div className="max-w-2xl mx-auto">
                            <h3 className="text-3xl font-black gradient-text mb-4">
                                Join The Deep Wave Community
                            </h3>
                            <p className="text-gray-400 mb-6">
                                Connect with music creators, discover new sounds, and be part of The Deep Wave's art.
                            </p>
                            <div className="flex justify-center">
                                <Link
                                    to="/register"
                                    className="btn-primary flex items-center justify-center gap-2"
                                >
                                    <Music className="w-5 h-5" />
                                    Create an Account
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/10 bg-gray-900/80">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                        <div className="text-center md:text-left">
                            <div className="text-gray-500 text-sm mb-2">
                                The Deep Wave - Arts & Sound
                            </div>
                            <div className="flex flex-wrap justify-center md:justify-start gap-4 text-xs text-gray-500">
                                <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
                                <Link to="/subscriptions" className="hover:text-white transition-colors">Subscriptions</Link>
                                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                                <Link to="/support" className="hover:text-white transition-colors">Support</Link>
                                <Link to="/sitemap" className="hover:text-white transition-colors">Sitemap</Link>
                                <Link to="/" className="hover:text-white transition-colors">About</Link>
                            </div>
                        </div>
                        
                        <div className="text-gray-500 text-sm text-center">
                            © {currentYear} The Deep Wave. All rights reserved.
                        </div>
                    </div>
                </div>
            </div>

        </footer>
    );
};

export default Footer;